import { Fragment } from 'react/jsx-runtime'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { PbshoppingMenuItem } from '@itsrever/rever-api'
import { Spinner } from '@heroui/react'
import { useAppSelector } from '@/redux/hooks'
import { DEFAULT_COUNTRY_CODE } from '../helpers/constants'
import StoreProductCard from './store-product-card'
import { useDebounce } from '@/hooks/use-debounce'
import EmptyCart from '@/app/full-catalog/empty-cart'
import { useStoreProducts } from '@/hooks/use-store-products'
import type { Filters } from '../desktop-full-catalog'

interface StoreProductsProps {
  filters: Filters
  selectedCategory?: PbshoppingMenuItem
  returnedItemId: string
}

export const StoreProducts = ({
  filters,
  selectedCategory,
  returnedItemId
}: StoreProductsProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const [searchParams] = useSearchParams()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const countryCode =
    useAppSelector((store) => store.return.returnAddress.country_code) ??
    DEFAULT_COUNTRY_CODE

  const orderCurrency = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )?.currency

  // Required for multi-site Salesforce merchants to resolve the correct site; optional otherwise.
  const orderReference = useAppSelector(
    (store) =>
      store.returnsApi.order.response?.order?.identification
        ?.customer_printed_order_id
  )

  const search = filters.search

  const debouncedSearch = useDebounce(search, 300)

  const { data, fetchNextPage, hasNextPage, status, isFetching } =
    useStoreProducts({
      ecommerceId,
      language,
      countryCode,
      orderCurrency,
      orderReference,
      resourceId: search ? undefined : selectedCategory?.resource_id,
      resourceType: search ? undefined : selectedCategory?.resource_type,
      filterText: search ? debouncedSearch : undefined,
      pageInfoStartCursor: searchParams.get('start_cursor') ?? undefined
    })

  const products = data?.pages.flatMap((page) => page.products) ?? []

  const noProductsFound = products[0] === undefined

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (status === 'error') {
    return (
      <div className="flex size-full flex-col items-center justify-center">
        <EmptyCart lightColor="#cbd5e1" darkColor="#f1f5f9" />
        <div className="space-y-1 pt-4">
          <h3 className="text-center text-xl font-semibold">
            {t('full_catalog.empty_category_title')}
          </h3>
          <p className="mx-auto max-w-80 text-center text-sm font-light text-slate-500">
            {t('full_catalog.empty_category_description')}
          </p>
        </div>
      </div>
    )
  }

  if (status === 'pending') {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <Spinner
          size="lg"
          color="current"
          classNames={{
            circle1: 'border-[3px] text-brand-700',
            circle2: 'border-[3px] text-brand-400'
          }}
        />
      </div>
    )
  }

  if (isHoff) {
    return noProductsFound ? (
      <div className="flex size-full flex-col items-center justify-center pt-10">
        <div className="space-y-1">
          <h3 className="text-center text-xl font-medium">
            {t('full_catalog.empty_category_title')}
          </h3>
          <p className="mx-auto max-w-80 text-center text-base font-light text-slate-500">
            {t('full_catalog.empty_category_description')}
          </p>
        </div>
      </div>
    ) : (
      <div className="space-y-10 pb-10">
        <ul className="grid gap-x-3.5 gap-y-8 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] md:[grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
          {data?.pages.map((page) => (
            <Fragment key={page.page_info?.end_cursor}>
              {page.products?.map((product) => (
                <li key={product.id}>
                  <StoreProductCard
                    product={product}
                    returnedItemId={returnedItemId}
                  />
                </li>
              ))}
            </Fragment>
          ))}
        </ul>
        <div className="flex justify-center">
          {hasNextPage ? (
            <button
              className="flex w-full max-w-36 items-center justify-center gap-3 rounded-md border border-solid border-neutral-800 px-4 py-2 text-base uppercase text-neutral-900 shadow-sm transition-background hover:bg-neutral-200/70"
              onClick={() => fetchNextPage()}
              disabled={isFetching}
            >
              {t('full_catalog.show_more')}
            </button>
          ) : (
            <p className="border-b-2 border-solid border-neutral-900 pb-4 text-sm text-neutral-900">
              {t('full_catalog.showing_of', {
                number: products.length
              })}
            </p>
          )}
        </div>
      </div>
    )
  }

  if (isMisako) {
    return noProductsFound ? (
      <div className="flex size-full flex-col items-center justify-center pt-10">
        <div className="space-y-1">
          <h3 className="text-center text-xl font-semibold">
            {t('full_catalog.empty_category_title')}
          </h3>
          <p className="mx-auto max-w-80 text-center text-sm font-light text-slate-500">
            {t('full_catalog.empty_category_description')}
          </p>
        </div>
      </div>
    ) : (
      <div className="flex-1 space-y-10 pb-8">
        <ul className="grid gap-x-3.5 gap-y-8 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]">
          {data?.pages.map((page) => (
            <Fragment key={page.page_info?.end_cursor}>
              {page.products?.map((product) => (
                <li key={product.id}>
                  <StoreProductCard
                    product={product}
                    returnedItemId={returnedItemId}
                  />
                </li>
              ))}
            </Fragment>
          ))}
        </ul>
        <div className="flex justify-center">
          {hasNextPage ? (
            <button
              className="flex w-full max-w-36 items-center justify-center gap-3 rounded-md border border-solid border-gray-500 px-4 py-2 text-base font-light uppercase tracking-wider text-gray-500 shadow-sm transition-background hover:bg-neutral-200/70"
              onClick={() => fetchNextPage()}
              disabled={isFetching}
            >
              {t('full_catalog.show_more')}
            </button>
          ) : (
            <p className="border-b-1 border-solid border-gray-400 pb-4 text-sm font-light tracking-wider text-gray-400">
              {t('full_catalog.showing_of', {
                number: products.length
              })}
            </p>
          )}
        </div>
      </div>
    )
  }

  return noProductsFound ? (
    <div className="flex size-full flex-1 flex-col items-center justify-center">
      <EmptyCart lightColor="#cbd5e1" darkColor="#f1f5f9" />
      <div className="space-y-1 pt-4">
        <h3 className="text-center text-xl font-semibold">
          {t('full_catalog.empty_category_title')}
        </h3>
        <p className="mx-auto max-w-80 text-center text-sm font-light text-slate-500">
          {t('full_catalog.empty_category_description')}
        </p>
      </div>
    </div>
  ) : (
    <div className="flex-1 space-y-10 pb-8 pt-1">
      <ul className="grid gap-x-2.5 gap-y-4 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] md:gap-x-3.5 md:gap-y-8 md:[grid-template-columns:repeat(auto-fill,minmax(210px,1fr))]">
        {data?.pages.map((page) => (
          <Fragment key={page.page_info?.end_cursor}>
            {page.products?.map((product) => (
              <li key={product.id}>
                <StoreProductCard
                  product={product}
                  returnedItemId={returnedItemId}
                />
              </li>
            ))}
          </Fragment>
        ))}
      </ul>
      <div className="flex justify-center">
        {hasNextPage ? (
          <button
            className="flex w-full max-w-36 items-center justify-center gap-3 rounded-md border border-solid border-gray-500 px-4 py-2 text-base font-medium text-gray-500 shadow-sm transition-background hover:bg-neutral-200/70"
            onClick={() => fetchNextPage()}
            disabled={isFetching}
          >
            {t('full_catalog.show_more')}
          </button>
        ) : (
          <p className="border-b-2 border-solid border-gray-400 pb-4 text-sm text-gray-400">
            {t('full_catalog.showing_of', {
              number: products.length
            })}
          </p>
        )}
      </div>
    </div>
  )
}
